import {
	OnInit,
	Input, Component, ViewEncapsulation
} from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import moment from 'moment';
import { MatDatepickerModule } from '@copo/mat-datepicker-new';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';

import { TranslationService } from '../../../../shared/services/translation.service';
import { FormPeriod } from '../../../models/form-offer';
import { PERIOD_TYPES } from '../../../models/offer';
import { AppWizardService } from '../../../services/app-wizard.service';

@Component({
	selector: 'copo-offer-utils-period',
	templateUrl: './period.component.html',
	styleUrl: './period.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		MatButtonModule,
		TranslateModule,
		MatFormFieldModule,
		MatSelectModule,
		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatIconModule,
		NgFor,
		MatButtonToggleModule,
		MatGridListModule
	]
})
export class PeriodComponent implements OnInit {
	@Input() isReadOnly = false;
	@Input() form!: FormPeriod;

	readonly semesters: string[] = [];
	readonly quarters: string[] = [];
	readonly months: string[] = [];

	selectedPeriodType = 'Monthly';
	selectedPeriodDisplay = '';
	dynamicDropdownValues: { year: string; values: { label: string; value: string }[] }[] = [];
	dynamicDropdownValuesFrom: { year: string; values: { label: string; value: string }[] }[] = [];
	dynamicDropdownValuesTo: { year: string; values: { label: string; value: string }[] }[] = [];
	fromMonth = '';
	toMonth = '';
	selectedDynamicValue = '';
	periodTypes;

	readonly allMonths = moment.monthsShort();

	constructor(
		readonly translationService: TranslationService,
		private readonly appWizardService: AppWizardService
	) {
		const semesterInitialLetter = translationService.getTR('eoffer-Semester');
		for (let indexSemester = 1; indexSemester <= 2; indexSemester++) {
			this.semesters.push(`${ semesterInitialLetter } ${ indexSemester }`);
		}
		const quarterInitialLetter = translationService.getTR('eoffer-Quarter');
		for (let indexQuarter = 1; indexQuarter <= 4; indexQuarter++) {
			this.quarters.push(`${ quarterInitialLetter } ${ indexQuarter }`);
		}

		for (const monthShort of moment.monthsShort()) {
			this.months.push(monthShort.charAt(0));
		}
		this.periodTypes = PERIOD_TYPES;
	}

	ngOnInit() {
		let startMonthValue = <string> this.form.getFormPeriodStartMonth().value;
		let endMonthValue = <string> this.form.getFormPeriodEndMonth().value;

		const currentDate = new Date();
		const currentMonth = currentDate.getMonth();
		const currentYear = currentDate.getFullYear();

		if (startMonthValue) {
			const [startYear, startMonth] = startMonthValue.split('-M').map(Number);
			const startDate = new Date(startYear, startMonth - 1, 1);
			if (startDate < currentDate) {
				this.form.getFormPeriodStartMonth().setValue('');
				startMonthValue = '';
			}
		}

		if (endMonthValue) {
			const [endYear, endMonth] = endMonthValue.split('-M').map(Number);
			const endDate = new Date(endYear, endMonth - 1, 1);
			if (endDate < currentDate) {
				this.form.getFormPeriodEndMonth().setValue('');
				endMonthValue = '';
			}
		}

		if (!this.form.getFormPeriodStartMonth().value || !this.form.getFormPeriodEndMonth().value) {
			this.selectedPeriodDisplay = this.translationService.getTR('global.none');
		}

		if (startMonthValue && endMonthValue) {
			const [startYear, startMonth] = startMonthValue.split('-M').map(Number);
			const [endYear, endMonth] = endMonthValue.split('-M').map(Number);
			const monthDifference = (endYear - startYear) * 12 + (endMonth - startMonth);

			this.selectedPeriodType =
					startYear === endYear && startMonth === endMonth ? 'Monthly'
						: monthDifference === 2 && [1, 4, 7, 10].includes(startMonth) ? 'Quarterly'
							: monthDifference === 5 && [1, 7].includes(startMonth) ? 'Semesterly'
								: startMonth === 1 && endMonth === 12 && startYear === endYear ? 'Annually'
									: 'Custom Period';

			switch (this.selectedPeriodType) {
			case 'Monthly':
				this.selectedDynamicValue = startMonthValue;

				break;

			case 'Quarterly':
				this.selectedDynamicValue = `${ startYear }-Q${ Math.floor((startMonth - 1) / 3) + 1 }`;

				break;

			case 'Semesterly':
				this.selectedDynamicValue = `${ startYear }-S${ Math.floor((startMonth - 1) / 6) + 1 }`;

				break;

			case 'Annually':
				this.selectedDynamicValue = `${ startYear }`;

				break;

			default:
				this.fromMonth = startMonthValue;
				this.toMonth = endMonthValue;
				this.dynamicDropdownValuesFrom =
					this.generateCustomPeriodDropdown(currentMonth + 1, currentYear, endMonth, endYear);
				this.dynamicDropdownValuesTo =
					this.generateCustomPeriodDropdown(startMonth, startYear, 11, currentYear + 1);
			}
		} else if (startMonthValue || endMonthValue) {
			const [startYear, startMonth] = startMonthValue.split('-M').map(Number);
			const [endYear, endMonth] = endMonthValue.split('-M').map(Number);
			this.selectedPeriodType = 'Custom Period';
			this.fromMonth = startMonthValue;
			this.toMonth = endMonthValue;
			this.dynamicDropdownValuesFrom =
				this.generateCustomPeriodDropdown(currentMonth + 1, currentYear, endMonth, endYear);
			this.dynamicDropdownValuesTo =
				this.generateCustomPeriodDropdown(startMonth, startYear, 11, currentYear + 1);
		}
		this.updateSelectedPeriodDisplay(startMonthValue, endMonthValue);
		this.updateDynamicDropdownValues();
	}

	onPeriodTypeChange(periodType: string) {
		this.selectedPeriodType = periodType;
		this.resetSelection();
		this.updateDynamicDropdownValues();
	}

	onDynamicValueChange(value: string) {
		this.selectedDynamicValue = value;
		this.setDatesBasedOnSelection();
	}

	updateDynamicDropdownValues() {
		const date = new Date();
		const currentMonth = date.getMonth();
		const currentYear = date.getFullYear();
		this.dynamicDropdownValuesFrom = [];
		this.dynamicDropdownValuesTo = [];
		this.dynamicDropdownValues = [];

		switch (this.selectedPeriodType) {
		case 'Custom Period':
			this.dynamicDropdownValuesFrom =
				this.generateCustomPeriodDropdown(currentMonth + 1, currentYear, 11, currentYear + 1);
			this.dynamicDropdownValuesTo =
				this.generateCustomPeriodDropdown(currentMonth + 1, currentYear, 11, currentYear + 1);
			break;
		case 'Monthly':
			for (let year = currentYear; year <= currentYear + 1; year++) {
				const startMonth = year === currentYear ? currentMonth + 1 : 0;
				const endMonth = year === currentYear + 1 ? 11 : 12;
				this.dynamicDropdownValues.push({
					year: year.toString(),
					values: this.months.slice(startMonth, endMonth + 1).map((_month, index) => ({
						label: moment().month(startMonth + index).format('MMMM'),
						value: `${ year }-M${ String(startMonth + index + 1).padStart(2, '0') }`
					}))
				});
			}
			break;
		case 'Quarterly': {
			const currentQuarter = Math.floor(currentMonth / 3);
			for (let year = currentYear; year <= currentYear + 1; year++) {
				const startQuarter = year === currentYear ? currentQuarter + 1 : 0;
				const endQuarter = year === currentYear + 1 ? 3 : 4;
				this.dynamicDropdownValues.push({
					year: year.toString(),
					values: this.quarters.slice(startQuarter, endQuarter + 1).map((quarter, index) => ({
						label: quarter,
						value: `${ year }-Q${ startQuarter + index + 1 }`
					}))
				});
			}
			break;
		}
		case 'Semesterly': {
			const currentSemester = Math.floor(currentMonth / 6);
			for (let year = currentYear; year <= currentYear + 1; year++) {
				const startSemester = year === currentYear ? currentSemester + 1 : 0;
				const endSemester = year === currentYear + 1 ? 1 : 2;
				this.dynamicDropdownValues.push({
					year: year.toString(),
					values: this.semesters.slice(startSemester, endSemester + 1).map((semester, index) => ({
						label: semester,
						value: `${ year }-S${ startSemester + index + 1 }`
					}))
				});
			}
			break;
		}
		case 'Annually':
			this.dynamicDropdownValues = [
				{
					year: '',
					values: [
						{ label: (currentYear + 1).toString(), value: `${ currentYear + 1 }` },
						{ label: (currentYear + 2).toString(), value: `${ currentYear + 2 }` }
					]
				}
			];
			break;
		default:
			this.dynamicDropdownValuesFrom = [];
			this.dynamicDropdownValuesTo = [];
			this.dynamicDropdownValues = [];
		}
	}

	setDatesBasedOnSelection() {
		let fromDate: Date = new Date();
		let toDate: Date = new Date();

		const parseDateRange = (year: number, startMonth: number, endMonth: number) => {
			fromDate = new Date(year, startMonth, 1);
			toDate = new Date(year, endMonth + 1, 0);
		};

		switch (this.selectedPeriodType) {
		case 'Monthly': {
			const selected = this.dynamicDropdownValues.flatMap(group => group.values)
				.find(item => item.value === this.selectedDynamicValue);
			if (selected) {
				const [year, month] = selected.value.split('-M');
				parseDateRange(Number(year), Number(month) - 1, Number(month) - 1);
			}
			break;
		}
		case 'Quarterly': {
			const selected = this.dynamicDropdownValues.flatMap(group => group.values)
				.find(item => item.value === this.selectedDynamicValue);
			if (selected) {
				const [year, quarter] = selected.value.split('-Q');
				const startMonth = (Number(quarter) - 1) * 3;
				parseDateRange(Number(year), startMonth, startMonth + 2);
			}
			break;
		}
		case 'Semesterly': {
			const selected = this.dynamicDropdownValues.flatMap(group => group.values)
				.find(item => item.value === this.selectedDynamicValue);
			if (selected) {
				const [year, semester] = selected.value.split('-S');
				const startMonth = (Number(semester) - 1) * 6;
				parseDateRange(Number(year), startMonth, startMonth + 5);
			}
			break;
		}
		case 'Annually': {
			const selected = this.dynamicDropdownValues.flatMap(group => group.values)
				.find(item => item.value === this.selectedDynamicValue);
			if (selected) {
				const year = Number(selected.value);
				parseDateRange(year, 0, 11);
			}
			break;
		}
		default:
			return;
		}

		const startMonthString = `${ fromDate.getFullYear() }-M${ String(fromDate.getMonth() + 1).padStart(2, '0') }`;
		const endMonthString = `${ toDate.getFullYear() }-M${ String(toDate.getMonth() + 1).padStart(2, '0') }`;

		this.selectedPeriodDisplay = this.selectedPeriodType === 'Monthly'
			? startMonthString
			: `${ startMonthString } - ${ endMonthString }`;

		this.emitSelectionChange(startMonthString, endMonthString);
	}

	onCustomPeriodChange() {
		this.appWizardService.getStateOfferForm().getFormFamilyByVolumes().clear();
		const currentYear = new Date().getFullYear();
		const currentMonth = new Date().getMonth();
		if (this.fromMonth) {
			const fromYear = Number.parseInt(this.fromMonth.split('-M')[0], 10);
			const fromIndex = Number.parseInt(this.fromMonth.split('-M')[1], 10) - 1;
			const toYear = this.toMonth ? Number.parseInt(this.toMonth.split('-M')[0], 10) : -1;
			const toIndex = this.toMonth ? Number.parseInt(this.toMonth.split('-M')[1], 10) - 1 : -1;

			if (toYear < fromYear || (toYear === fromYear && toIndex < fromIndex)) {
				this.toMonth = '';
			}
			this.dynamicDropdownValuesTo =
				this.generateCustomPeriodDropdown(fromIndex, fromYear, 11, currentYear + 1);
		}

		if (this.toMonth) {
			const toYear = Number.parseInt(this.toMonth.split('-M')[0], 10);
			const toIndex = Number.parseInt(this.toMonth.split('-M')[1], 10) - 1;
			const fromYear = this.fromMonth ? Number.parseInt(this.fromMonth.split('-M')[0], 10) : -1;
			const fromIndex = this.fromMonth ? Number.parseInt(this.fromMonth.split('-M')[1], 10) - 1 : -1;

			if (fromYear > toYear || (fromYear === toYear && fromIndex > toIndex)) {
				this.fromMonth = '';
			}

			this.dynamicDropdownValuesFrom =
				this.generateCustomPeriodDropdown(currentMonth + 1, currentYear, toIndex, toYear);
		}
		this.updateSelectedPeriodDisplay(this.fromMonth, this.toMonth);
		this.emitSelectionChange(this.fromMonth, this.toMonth);
	}

	generateCustomPeriodDropdown(startMonth: number, startYear: number, endMonth: number, endYear: number) {
		const dropdownValues = [];

		for (let year = startYear; year <= endYear; year++) {
			const adjustedStartMonth = year === startYear ? startMonth : 0;
			const adjustedEndMonth = year === endYear ? endMonth : 11;

			if (adjustedStartMonth <= adjustedEndMonth) {
				dropdownValues.push({
					year: year.toString(),
					values: this.months.slice(adjustedStartMonth, adjustedEndMonth + 1).map((_month, index) => ({
						label: moment().month(adjustedStartMonth + index).format('MMMM'),
						value: `${ year }-M${ String(adjustedStartMonth + index + 1).padStart(2, '0') }`
					}))
				});
			}
		}
		return dropdownValues;
	}

	formatPeriodValue(value: string): string {
		if (!value) {
			return '';
		}
		const [year, period] = value.split('-');
		if (period) {
			if (period.startsWith('M')) {
				const month = Number(period.slice(1)) - 1;
				return `${ moment().month(month).format('MMMM') } (${ year })`;
			} else if (period.startsWith('Q')) {
				const quarter = period.slice(1);
				const quarterInitialLetter = this.translationService.getTR('eoffer-Quarter');
				return `${ quarterInitialLetter } ${ quarter } (${ year })`;
			} else if (period.startsWith('S')) {
				const semester = period.slice(1);
				const semesterInitialLetter = this.translationService.getTR('eoffer-Semester');
				return `${ semesterInitialLetter } ${ semester } (${ year })`;
			}
		}
		return year;
	}

	private resetSelection() {
		this.selectedDynamicValue = '';
		this.fromMonth = '';
		this.toMonth = '';
		this.selectedPeriodDisplay = this.translationService.getTR('global.none');
		this.emitSelectionChange(undefined, undefined);
		this.appWizardService.getStateOfferForm().getFormFamilyByVolumes().clear();
	}

	private emitSelectionChange(startDate: string | undefined, endDate: string | undefined) {
		this.form.getFormPeriodStartMonth().setValue(startDate, {
			onlySelf: false, emitEvent: false, emitModelToViewChange: false, emitViewToModelChange: false
		});
		this.form.getFormPeriodEndMonth().setValue(endDate, {
			onlySelf: false, emitEvent: false, emitModelToViewChange: false, emitViewToModelChange: false
		});
		this.form.updateValueAndValidity();
	}

	private updateSelectedPeriodDisplay(fromMonth: string, toMonth?: string) {
		if (fromMonth && toMonth) {
			this.selectedPeriodDisplay = `${ fromMonth } - ${ toMonth }`;
		} else if (fromMonth) {
			this.selectedPeriodDisplay = `${ fromMonth } -`;
		} else if (toMonth) {
			this.selectedPeriodDisplay = `- ${ toMonth }`;
		} else {
			this.selectedPeriodDisplay = this.translationService.getTR('global.none');
		}
	}
}
